import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { ANIMAL_TYPES, ANIMAL_LABELS, ANIMAL_PLURALS } from '../../lib/animals';
import { PawPrint, Users, Plus, Loader2 } from 'lucide-react';
import AnimalDialog from './AnimalDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Booking-form animal picker.
 *
 * Two modes:
 *  - "saved":  client picks 1+ of their saved animals (only for logged-in clients)
 *  - "group":  client books by quantity + type (e.g. "20 cows")
 *
 * Emits onChange({mode, selectedAnimalIds, animalType, animalQuantity, groupDescription})
 */
export default function AnimalPicker({ value, onChange }) {
  const { user, token } = useAuth();
  const isClient = user?.role === 'client';

  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(value?.mode || (isClient ? 'saved' : 'group'));
  const [showCreate, setShowCreate] = useState(false);

  const selectedAnimalIds = value?.selectedAnimalIds || [];
  const animalType = value?.animalType || 'HORSE';
  const animalQuantity = value?.animalQuantity ?? 1;
  const groupDescription = value?.groupDescription || '';

  useEffect(() => {
    if (!isClient) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/client/animals`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAnimals(res.data || []))
      .catch(() => setAnimals([]))
      .finally(() => setLoading(false));
  }, [isClient, token, showCreate]);

  const emitSaved = (ids) =>
    onChange({
      mode: 'saved',
      selectedAnimalIds: ids,
      animalType: null,
      animalQuantity: null,
      groupDescription: null,
    });

  const emitGroup = (partial) =>
    onChange({
      mode: 'group',
      selectedAnimalIds: [],
      animalType: partial.animalType ?? animalType,
      animalQuantity: partial.animalQuantity ?? animalQuantity,
      groupDescription: partial.groupDescription ?? groupDescription,
    });

  const toggleId = (id) => {
    const next = selectedAnimalIds.includes(id)
      ? selectedAnimalIds.filter((x) => x !== id)
      : [...selectedAnimalIds, id];
    emitSaved(next);
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === 'saved') emitSaved(selectedAnimalIds);
    else emitGroup({});
  };

  // Anonymous: only group mode available
  if (!isClient) {
    return (
      <Card data-testid="animal-picker-anon">
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <Label className="m-0">How many animals?</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Tip:{' '}
            <a href="/client/signup" className="text-primary hover:underline">
              create a client account
            </a>{' '}
            to save your animals and reuse them across providers.
          </p>
          <GroupFields
            animalType={animalType}
            animalQuantity={animalQuantity}
            groupDescription={groupDescription}
            emit={emitGroup}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Tabs value={mode} onValueChange={switchMode} data-testid="animal-picker">
        <TabsList className="grid w-full grid-cols-2 max-w-sm">
          <TabsTrigger value="saved" data-testid="picker-tab-saved">
            <PawPrint className="h-3 w-3 mr-1" /> Saved Animals
          </TabsTrigger>
          <TabsTrigger value="group" data-testid="picker-tab-group">
            <Users className="h-3 w-3 mr-1" /> Group / Quantity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="saved" className="mt-3">
          {loading ? (
            <div className="flex items-center justify-center p-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading your animals...
            </div>
          ) : animals.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <PawPrint className="h-10 w-10 mx-auto text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">
                  You don't have any saved animals yet.
                </p>
                <Button size="sm" onClick={() => setShowCreate(true)} data-testid="picker-add-animal">
                  <Plus className="h-4 w-4 mr-1" /> Add Your First Animal
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {animals.map((a) => {
                const checked = selectedAnimalIds.includes(a.id);
                return (
                  <label
                    key={a.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:border-primary transition-colors ${
                      checked ? 'border-primary bg-primary/5' : ''
                    }`}
                    data-testid={`picker-animal-${a.id}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggleId(a.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{a.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {ANIMAL_LABELS[a.animalType] || a.animalType}
                          {a.breed ? ` • ${a.breed}` : ''}
                          {a.age != null ? ` • ${a.age}y` : ''}
                        </span>
                      </div>
                    </div>
                  </label>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreate(true)}
                data-testid="picker-add-another"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Another Animal
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="group" className="mt-3">
          <Card>
            <CardContent className="pt-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Use this for larger groups when you don't need individual records (e.g. herd
                vaccinations, group hoof trims).
              </p>
              <GroupFields
                animalType={animalType}
                animalQuantity={animalQuantity}
                groupDescription={groupDescription}
                emit={emitGroup}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AnimalDialog open={showCreate} onOpenChange={setShowCreate} onSaved={() => {}} />
    </>
  );
}

function GroupFields({ animalType, animalQuantity, groupDescription, emit }) {
  const plural = ANIMAL_PLURALS[animalType] || 'animals';
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Quantity</Label>
          <Input
            type="number"
            min={1}
            max={10000}
            value={animalQuantity}
            onChange={(e) => emit({ animalQuantity: parseInt(e.target.value, 10) || 1 })}
            data-testid="group-quantity-input"
          />
        </div>
        <div>
          <Label>Animal Type</Label>
          <Select value={animalType} onValueChange={(v) => emit({ animalType: v })}>
            <SelectTrigger data-testid="group-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANIMAL_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Booking for <strong>{animalQuantity}</strong> {plural}.
      </p>
      <div>
        <Label>Group description (optional)</Label>
        <Textarea
          rows={2}
          value={groupDescription || ''}
          onChange={(e) => emit({ groupDescription: e.target.value })}
          placeholder="e.g. mixed dairy cows, calves only, all yearlings…"
          maxLength={1000}
        />
      </div>
    </>
  );
}
